import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine Protection
    FaTruckPickup,      // For 24x7 Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
    FaCarSide,       // For Claims and service centers
} from "react-icons/fa";

import {
    GiScooter, // from react-icons/gi
  } from "react-icons/gi";
  import {
    Sparkles,
    Flame,
    Mountain,
    Bike,
    ShieldCheck,
    Rocket,
    GaugeCircle,
    Landmark,
    Wind,
    CarFront,
  } from "lucide-react";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const YamahaTwoWheelerInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is it mandatory to have insurance for Yamaha bikes and scooters?",
      answer: "Yes, third-party insurance is legally required to ride on Indian roads.",
    },
    {
      question: "What is the difference between third-party and comprehensive Yamaha insurance?",
      answer: "Third-party covers only damage to others. Comprehensive includes own damage, theft, disasters, and more.",
    },
    {
      question: "Which insurance add-ons are ideal for Yamaha R15 owners?",
      answer: "Zero depreciation, roadside assistance, and engine protection are highly recommended.",
    },
    {
      question: "Does comprehensive insurance cover helmet theft?",
      answer: "No, helmets and gear are usually not covered unless you opt for specific gear protection add-ons.",
    },
    {
      question: "How can I buy Yamaha insurance online?",
      answer: "You can buy or renew Yamaha insurance online through trusted platforms like TheFinMaster in just a few steps.",
    },
    {
      question: "What happens if I miss my Yamaha insurance renewal date?",
      answer: "Your policy will lapse, and you may need a vehicle inspection before buying a new one. You also lose your NCB benefits.",
    },
    {
      question: "Are modified Yamaha bikes eligible for insurance?",
      answer: "Yes, but modifications must be declared and may slightly affect the premium.",
    },
    {
      question: "What is IDV in Yamaha insurance?",
      answer: "Insured Declared Value is your bike’s current market value and forms the basis for your premium and claim payout.",
    },
    {
      question: "How is the premium calculated for Yamaha scooters like Fascino or RayZR?",
      answer: "It depends on the bike’s age, IDV, location, engine capacity, and coverage type.",
    },
    {
      question: "Can I transfer Yamaha insurance during vehicle resale?",
      answer: "Yes, insurance can be transferred to the new owner along with ownership documents.",
    },
    {
      question: "Is roadside assistance useful for Yamaha MT or Aerox riders?",
      answer: "Absolutely. These riders often cover longer distances, making RSA a smart choice.",
    },
    {
      question: "How do I get cashless repair service for my Yamaha?",
      answer: "Most insurers offer cashless claims at authorized Yamaha service centers.",
    },
    {
      question: "Do Yamaha electric vehicles need separate insurance?",
      answer: "Currently, Yamaha does not offer EVs in India, but future models will require EV-specific coverage.",
    },
    {
      question: "Can I buy Yamaha insurance without visiting an office?",
      answer: "Yes, most providers offer completely digital policy issuance with eKYC.",
    },
    {
      question: "What is NCB and how can Yamaha riders use it?",
      answer: "NCB (No Claim Bonus) gives you a discount on renewal if you haven't made any claims in the previous year.",
    },
    {
      question: "What’s the ideal insurance for Yamaha Aerox 155?",
      answer: "Comprehensive policy with zero depreciation and engine cover is best for a high-performance scooter like Aerox.",
    },
    {
      question: "Does insurance cover damages from natural disasters?",
      answer: "Only comprehensive policies cover flood, fire, earthquakes, and similar events.",
    },
    {
      question: "Is there a penalty for riding a Yamaha without valid insurance?",
      answer: "Yes, you may face heavy fines, license suspension, or even vehicle seizure in some cases.",
    },
    {
      question: "How soon can I file a claim after an accident?",
      answer: "Immediately. You should notify your insurer within 24–48 hours to avoid any rejection.",
    },
    {
      question: "Does TheFinMaster assist with Yamaha insurance comparison?",
      answer: "Yes, TheFinMaster offers tools to compare Yamaha insurance plans, pricing, and coverage — all in one place.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description: "Ideal for R15, Aerox, and MT owners to avoid deductions.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description: "Important for scooters and city riders facing flood-prone areas.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "24x7 Roadside Assistance",
      description: "Highly recommended for long-distance riders.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers non-reimbursed items like nuts, bolts, oils, etc.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Offers full invoice value in case of total loss or theft.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
  const benefits = [

  {
    title: "Yamaha R15 V4 / R15M",
    desc: "Race-inspired premium 150cc motorcycle.",
    icon: <Rocket className="w-7 h-7 text-red-500" />,
    gradient: "from-red-50 to-red-100",
  },
  {
    title: "Yamaha MT-15",
    desc: " Naked streetfighter with a bold design",
    icon: <ShieldCheck className="w-7 h-7 text-orange-500" />,
    gradient: "from-orange-50 to-orange-100",
  },
  {
    title: "Yamaha FZ / FZ-S",
    desc: "Stylish and muscular 150cc commuter",
    icon: <Bike className="w-7 h-7 text-indigo-500" />,
    gradient: "from-indigo-50 to-indigo-100",
  },
  {
    title: "Yamaha Aerox 155",
    desc: " India’s first maxi-sports scooter",
    icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
    gradient: "from-cyan-50 to-cyan-100",
  },
  {
    title: "Yamaha Fascino 125",
    desc: " Retro-modern scooter with hybrid power",
    icon: <Landmark className="w-7 h-7 text-pink-500" />,
    gradient: "from-pink-50 to-pink-100",
  },
  {
    title: "Yamaha RayZR 125",
    desc: "Sporty scooter for youth riders",
    icon: <Wind className="w-7 h-7 text-teal-500" />,
    gradient: "from-teal-50 to-teal-100",
  },
];
const yamahainsurance = [
    {
      name: "R15",
      path: "/yamaha-r15-bike-insurance", 
      image: "/assets/yamahar15.jpeg",
    },
   {
      name: "MT15V2",
      path: "/yamaha-mt15v2-bike-insurance", 
      image: "/assets/yamahamt15v2.jpeg",
    },
    {
      name: "FZSFI",
      path: "/yamaha-fzs-bike-insurance", 
      image: "/assets/yamahafzsfi.jpeg",
    },
    {
      name: "Aerox155",
      path: "/yamaha-aerox155-bike-insurance", 
      image: "/assets/yamahaaerox155.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Yamaha Two-Wheeler Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Yamaha two-wheeler insurance options in India. Compare quotes, customize coverage, and buy online to protect your Yamaha bike or scooter."
        />
        <meta
          name="keywords"
          content="yamaha two-wheeler insurance, bike insurance india, yamaha r15 insurance, yamaha mt15 insurance, yamaha aerox insurance, two-wheeler insurance quotes"
        />
           <link rel="canonical" href="https://thefinmaster.com/yamaha-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/yamaha-two-wheeler-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Yamaha Two-Wheeler Insurance in India" />
        <meta property="og:description" content="Compare and buy Yamaha two-wheeler insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/yamaha-two-wheeler-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Yamaha Two-Wheeler Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Yamaha Two-Wheeler Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Yamaha is renowned for its sporty performance, stylish design, and reliable engineering. Whether you ride a commuter-friendly Saluto or a performance-packed R15, keeping your bike protected starts with the right insurance. This guide takes you through everything you need to know about insuring a Yamaha bike, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/yamahainsurance.jpeg"
                alt="Yamaha Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-slate-800">
        About Yamaha in India
        </h2>
        <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
        Yamaha has established itself as a performance-first brand, popular among young riders and commuters alike. Its offerings combine innovation, fuel efficiency, and iconic design, making Yamaha bikes and scooters a top choice in both urban and semi-urban markets.
        </p>
              </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {benefits.map((item, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
          >
            <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
              {item.icon}
            </div>
            <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
              {item.title}
            </h3>
            <p className="text-sm text-slate-600 text-center leading-relaxed">
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Highlight Banner */}
      {/* <div className="mt-12 p-5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white text-center shadow-sm">
        <p className="text-blue-700 text-sm font-medium">
        Suzuki models appeal to daily commuters, style-conscious youth, and even touring enthusiasts.        </p>
      </div> */}
    </section>
    <div className="p-4 sm:p-8 bg-white">
                                                                            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                                                              Choose Your Bike Brand
                                                                            </h2>
                                                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                                                              {yamahainsurance.map((calc, index) => (
                                                                                <motion.div
                                                                                  key={index}
                                                                                  whileHover={{ scale: 1.04 }}
                                                                                  initial={{ opacity: 0, y: 20 }}
                                                                                  animate={{ opacity: 1, y: 0 }}
                                                                                  transition={{ duration: 0.3, delay: index * 0.05 }}
                                                                                >
                                                                                  <Link
                                                                                    to={calc.path}
                                                                                    className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                                                  >
                                                                                    <img
                                                                                      src={calc.image}
                                                                                      alt={calc.name}
                                                                                      className="w-16 h-16 object-contain mb-2"
                                                                                    />
                                                                                    <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                                                      {calc.name}
                                                                                    </span>
                                                                                  </Link>
                                                                                </motion.div>
                                                                              ))}
                                                                            </div>
                                                                          </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Yamaha Owners Should Prioritize Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Owning a Yamaha is more than just transportation — it's about style and performance. But with that comes the responsibility of protecting your investment. Here’s why insurance is non-negotiable:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Protects against theft, accidents, and natural calamities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Ensures financial safety during repairs or liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Required by law to ride on Indian roads</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Offers peace of mind during long-distance or daily commutes</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Enables smooth claims and access to cashless Yamaha service centers</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your Yamaha stays protected, legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
       <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="max-w-xl space-y-6 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                Calculate Your TwoWheeler Insurance <br />
                in <span className="text-blue-600">Seconds</span>
              </h2>
              <p className="text-gray-700 text-lg">
                Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
              </p>
              <Link
                to="/financial-tools-calculators/bike-insurance-calculator"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
              >
                Start Calculating <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="space-y-4 text-left">
              {features.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Yamaha Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below are the estimated insured declared values (IDV) and starting insurance premiums for key Yamaha models:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Yamaha Fascino 125", category: "Retro Scooter", idv: "75,000 – 90,000", premium: "₹900 – ₹1,300" },
              { model: "Yamaha RayZR 125", category: "Youth Scooter", idv: "85,000 – 1.00 Lakh", premium: "₹1,000 – ₹1,350" },
              { model: "Yamaha FZ / FZ-S", category: "Street Commuter Bike", idv: "1.1 – 1.3 Lakh", premium: "₹1,200 – ₹1,600" },
              { model: "Yamaha R15 V4 / R15M", category: "Premium Sports Bike", idv: "1.8 – 2.1 Lakh", premium: "₹1,800 – ₹2,400" },
              { model: "Yamaha MT-15 V2", category: "Naked Street Bike", idv: "1.6 – 1.9 Lakh", premium: "₹1,700 – ₹2,200" },
              { model: "Yamaha Aerox 155", category: "Sports Scooter", idv: "1.3 – 1.5 Lakh", premium: "₹1,400 – ₹1,900" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Yamaha Model</th>
                  <th className="p-3 border border-gray-300 text-left">Segment</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Yamaha Fascino 125", "Retro Scooter", "75,000 – 90,000", "₹900 – ₹1,300"],
                  ["Yamaha RayZR 125", "Youth Scooter", "85,000 – 1.00 Lakh", "₹1,000 – ₹1,350"],
                  ["Yamaha FZ / FZ-S", "Street Commuter Bike", "1.1 – 1.3 Lakh", "₹1,200 – ₹1,600"],
                  ["Yamaha R15 V4 / R15M", "Premium Sports Bike", "1.8 – 2.1 Lakh", "₹1,800 – ₹2,400"],
                  ["Yamaha MT-15 V2", "Naked Street Bike", "1.6 – 1.9 Lakh", "₹1,700 – ₹2,200"],
                  ["Yamaha Aerox 155", "Sports Scooter", "1.3 – 1.5 Lakh", "₹1,400 – ₹1,900"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons Worth Considering for Yamaha Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Yamaha bikes often feature advanced technology and premium components, so selecting the right add-ons can significantly enhance coverage:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Trusted Insurers Offering Yamaha Two-Wheeler Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Several reputed insurance companies offer competitive and reliable coverage for Yamaha owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Strong claim reputation and vast garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Great for app-based policy management.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Transparent and quick claim settlement.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">TATA AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Comprehensive packages for sports and premium bikes.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Fast claim settlement and attractive add-on options.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Get the Best Deal on Yamaha Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To get the best premium rates and coverage benefits, follow these smart tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare multiple insurer quotes on TheFinMaster.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Opt for long-term policies to reduce annual renewals.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Use accumulated No Claim Bonus (NCB) to lower premiums.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install anti-theft devices to qualify for extra discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Select only the add-ons you genuinely need based on usage.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Yamaha Bike/Scooter Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Ensure the following documents are ready for a smooth buying or renewal process:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle RC (Registration Certificate)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous year’s insurance (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PUC (Pollution Under Control) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-sized photograph (in some cases)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs about Yamaha Two-Wheeler Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Yamaha bikes and scooters represent a lifestyle of performance, style, and freedom. To keep that experience worry-free, investing in the right two-wheeler insurance plan is a must. A balanced policy not only ensures legal compliance but also shields you from repair costs, theft, and financial strain.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                With TheFinMaster, finding the best Yamaha insurance becomes easy. Compare top insurers, choose tailored add-ons, and enjoy complete peace of mind on every ride.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default YamahaTwoWheelerInsurance;